import logger, { LogContext } from '../services/logger';
import {
  clearCallContext,
  getCallContext,
  nextCallAttempt,
  setCallContext,
} from '../services/callCorrelation';

/**
 * Helper functions for common logging patterns
 */

// Component lifecycle logging
export const logComponentMount = (componentName: string, props?: any) => {
  logger.debug(`${componentName} mounted`, {
    source: 'component',
    event: 'mount',
    component: componentName,
    props: props ? JSON.stringify(props) : undefined,
  });
};

export const logComponentUnmount = (componentName: string) => {
  logger.debug(`${componentName} unmounted`, {
    source: 'component',
    event: 'unmount',
    component: componentName,
  });
};

// Navigation logging
export const logNavigation = (from: string, to: string, userId?: string) => {
  logger.info(`Navigation: ${from} -> ${to}`, {
    source: 'navigation',
    event: 'route_change',
    from,
    to,
    userId,
  });
};

// Authentication logging
export const logAuthSuccess = (userId: string, tenant: string, method: string = 'keycloak', sipInstanceId?: string | null) => {
  logger.info('Authentication successful', {
    source: 'auth',
    event: 'login_success',
    userId,
    tenant,
    method,
    sipInstanceId: sipInstanceId ?? undefined,
  });
};

export const logAuthFailure = (reason: string, method: string = 'keycloak', sipInstanceId?: string | null) => {
  logger.warn('Authentication failed', {
    source: 'auth',
    event: 'login_failure',
    reason,
    method,
    sipInstanceId: sipInstanceId ?? undefined,
  });
};

export const logAuthLogout = (userId?: string, tenant?: string) => {
  logger.info('User logged out', {
    source: 'auth',
    event: 'logout',
    userId,
    tenant,
  });
};

// API request logging
export const logApiRequest = (method: string, url: string, status?: number, duration?: number) => {
  const level = status && status >= 400 ? 'warn' : 'info';
  const message = `API ${method} ${url} - ${status || 'pending'}`;
  
  logger[level](message, {
    source: 'api',
    event: 'request',
    method,
    url,
    status,
    duration,
  });
};

export const logApiError = (method: string, url: string, error: any, status?: number) => {
  logger.error(`API ${method} ${url} failed`, {
    source: 'api',
    event: 'error',
    method,
    url,
    status,
    error: error?.message || String(error),
  });
};

const callContexts = new Map<string, ReturnType<typeof getCallContext>>();

// Call/SIP logging
/**
 * Start of a call, and the point the correlation context opens.
 *
 * `traceId` is the trace root channels mints for the call and carries
 * outward; it falls back to the call id, which is what the backend derives it
 * from today, so a call that arrives without one still correlates. From here
 * every log entry this tab writes carries the trace until logCallEnd, so the
 * whole call - not just the lines someone remembered to tag - joins to the
 * backend events that caused it.
 */
export const logCallStart = (
  callId: string,
  direction: 'inbound' | 'outbound',
  phoneNumber?: string,
  traceId?: string,
) => {
  setCallContext({ trace_id: traceId ?? callId, call_id: callId });
  nextCallAttempt();
  callContexts.set(callId, getCallContext());
  if (callContexts.size > 32) callContexts.delete(callContexts.keys().next().value!);
  logger.info('Call started', {
    source: 'sip',
    event: 'call_start',
    callId,
    direction,
    phoneNumber,
  });
};

export const logCallEnd = (callId: string, duration?: number, reason?: string) => {
  logger.info('Call ended', {
    ...callContexts.get(callId),
    source: 'sip',
    event: 'call_end',
    callId,
    duration,
    reason,
  });
  // Cleared AFTER the entry, so the call's own end still carries its trace.
  callContexts.delete(callId);
  if (getCallContext().call_id === callId) clearCallContext();
};

export const logCallError = (callId: string, error: string, phoneNumber?: string) => {
  logger.error('Call error occurred', {
    source: 'sip',
    event: 'call_error',
    callId,
    error,
    phoneNumber,
  });
};

// Chat/Matrix logging
export const logChatMessage = (roomId: string, messageType: string, userId?: string) => {
  logger.debug('Chat message sent', {
    source: 'chat',
    event: 'message_sent',
    roomId,
    messageType,
    userId,
  });
};

export const logChatError = (error: string, roomId?: string, operation?: string) => {
  logger.error('Chat error occurred', {
    source: 'chat',
    event: 'error',
    error,
    roomId,
    operation,
  });
};

export const logChatConnection = (status: 'connected' | 'disconnected' | 'reconnecting', userId?: string) => {
  logger.info(`Chat ${status}`, {
    source: 'chat',
    event: 'connection',
    status,
    userId,
  });
};

// User interaction logging
export const logUserInteraction = (action: string, component: string, details?: any) => {
  logger.debug(`User ${action}`, {
    source: 'user',
    event: 'interaction',
    action,
    component,
    details: details ? JSON.stringify(details) : undefined,
  });
};

// Error boundary logging
export const logErrorBoundary = (error: Error, errorInfo: any, component?: string) => {
  logger.error('Error boundary caught error', {
    source: 'error_boundary',
    event: 'error',
    error: error.message,
    stack: error.stack,
    component,
    errorInfo: JSON.stringify(errorInfo),
  });
};

// Performance logging
export const logPerformance = (operation: string, duration: number, component?: string) => {
  const level = duration > 1000 ? 'warn' : 'debug'; // Warn if operation takes > 1 second
  
  logger[level](`Performance: ${operation} took ${duration}ms`, {
    source: 'performance',
    event: 'timing',
    operation,
    duration,
    component,
  });
};

// Integration logging (for third-party services)
export const logIntegration = (service: string, operation: string, status: 'success' | 'failure', details?: any) => {
  const level = status === 'success' ? 'info' : 'error';
  
  logger[level](`${service} integration ${status}`, {
    source: 'integration',
    event: operation,
    service,
    status,
    details: details ? JSON.stringify(details) : undefined,
  });
};

// Generic error logging with context
export const logError = (error: Error | string, context: Partial<LogContext> = {}) => {
  const errorMessage = typeof error === 'string' ? error : error.message;
  const stack = typeof error === 'object' && error.stack ? error.stack : undefined;
  
  logger.error(errorMessage, {
    ...context,
    source: context.source || 'error',
    event: context.event || 'error',
    stack,
  });
};

// Development helpers (only log in development)
export const logDev = (message: string, context?: LogContext) => {
  if (import.meta.env.DEV) {
    logger.debug(`[DEV] ${message}`, {
      ...context,
      source: context?.source || 'dev',
      event: context?.event || 'debug',
    });
  }
};

export const logDevError = (message: string, error?: any) => {
  if (import.meta.env.DEV) {
    logger.error(`[DEV] ${message}`, {
      source: 'dev',
      event: 'error',
      error: error?.message || String(error),
    });
  }
};
/** Subscribe at newRTCSession, including failures before a call is answered. */
export const trackSipCall = (
  session: {on(event: string, listener: (event: {cause?: string}) => void): unknown},
  callId: string,
  direction: 'inbound' | 'outbound',
): void => {
  logCallStart(callId, direction);
  let ended = false;
  const finish = (event: {cause?: string}) => {
    if (ended) return;
    ended = true;
    logCallEnd(callId, undefined, event?.cause);
  };
  session.on('ended', finish);
  session.on('failed', finish);
};
